import React from 'react';
import { Link } from 'react-router-dom';
import { Eye, CheckCircle, XCircle } from 'lucide-react';

interface Application {
  id: string;
  application_number: string;
  debtor_id: string;
  credit_type: string;
  requested_amount: number;
  total_score: number;
  analyst_recommendation: string;
}

interface ApprovalTableProps {
  applications: Application[];
  loading: boolean;
  getDebtorName: (id: string) => string;
  onApprove: (app: Application) => void;
  onReject: (app: Application) => void;
}

const creditTypeLabels: Record<string, string> = {
  working_capital: 'Modal Kerja',
  investment: 'Investasi',
  consumer: 'Konsumtif',
  kpr: 'KPR',
  kendaraan: 'Kendaraan',
  multiguna: 'Multiguna',
};

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0,
  }).format(value || 0);
};

const getScoreColor = (score: number) => {
  if (score >= 70) return 'text-green-600';
  if (score >= 50) return 'text-amber-600';
  return 'text-red-600';
};

export default function ApprovalTable({
  applications,
  loading,
  getDebtorName,
  onApprove,
  onReject,
}: ApprovalTableProps) {
  if (loading) {
    return (
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50 border-b">
              <th className="text-left p-4 font-medium text-gray-600">No. Aplikasi</th>
              <th className="text-left p-4 font-medium text-gray-600">Debitur</th>
              <th className="text-left p-4 font-medium text-gray-600">Jenis</th>
              <th className="text-left p-4 font-medium text-gray-600">Plafon</th>
              <th className="text-left p-4 font-medium text-gray-600">Skor</th>
              <th className="text-left p-4 font-medium text-gray-600">Rekomendasi</th>
              <th className="text-center p-4 font-medium text-gray-600">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {Array(3)
              .fill(0)
              .map((_, i) => (
                <tr key={i} className="border-b">
                  <td className="p-4">
                    <div className="h-4 w-32 bg-gray-200 rounded animate-pulse" />
                  </td>
                  <td className="p-4">
                    <div className="h-10 w-40 bg-gray-200 rounded animate-pulse" />
                  </td>
                  <td className="p-4">
                    <div className="h-6 w-24 bg-gray-200 rounded animate-pulse" />
                  </td>
                  <td className="p-4">
                    <div className="h-4 w-28 bg-gray-200 rounded animate-pulse" />
                  </td>
                  <td className="p-4">
                    <div className="h-4 w-16 bg-gray-200 rounded animate-pulse" />
                  </td>
                  <td className="p-4">
                    <div className="h-6 w-24 bg-gray-200 rounded animate-pulse" />
                  </td>
                  <td className="p-4">
                    <div className="h-8 w-32 bg-gray-200 rounded animate-pulse mx-auto" />
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="bg-gray-50 border-b">
            <th className="text-left p-4 font-medium text-gray-600">No. Aplikasi</th>
            <th className="text-left p-4 font-medium text-gray-600">Debitur</th>
            <th className="text-left p-4 font-medium text-gray-600">Jenis</th>
            <th className="text-left p-4 font-medium text-gray-600">Plafon</th>
            <th className="text-left p-4 font-medium text-gray-600">Skor</th>
            <th className="text-left p-4 font-medium text-gray-600">Rekomendasi</th>
            <th className="text-center p-4 font-medium text-gray-600">Aksi</th>
          </tr>
        </thead>
        <tbody>
          {applications.length === 0 ? (
            <tr>
              <td colSpan={7} className="text-center py-12">
                <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-300" />
                <p className="text-gray-500">Tidak ada aplikasi yang menunggu approval</p>
              </td>
            </tr>
          ) : (
            applications.map(app => (
              <tr key={app.id} className="border-b hover:bg-gray-50 transition-colors">
                <td className="p-4 font-mono text-sm">{app.application_number || '-'}</td>
                <td className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                      <span className="font-semibold text-blue-700">
                        {getDebtorName(app.debtor_id)?.charAt(0) || 'D'}
                      </span>
                    </div>
                    <span className="font-medium">{getDebtorName(app.debtor_id)}</span>
                  </div>
                </td>
                <td className="p-4">
                  <span className="px-2 py-1 text-xs font-medium border rounded">
                    {creditTypeLabels[app.credit_type] || app.credit_type}
                  </span>
                </td>
                <td className="p-4 font-medium">{formatCurrency(app.requested_amount)}</td>
                <td className="p-4">
                  <div className="flex items-center gap-2">
                    <span className={`text-lg font-bold ${getScoreColor(app.total_score)}`}>
                      {app.total_score || '-'}
                    </span>
                    <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full ${
                          app.total_score >= 70
                            ? 'bg-green-500'
                            : app.total_score >= 50
                              ? 'bg-amber-500'
                              : 'bg-red-500'
                        }`}
                        style={{ width: `${app.total_score || 0}%` }}
                      />
                    </div>
                  </div>
                </td>
                <td className="p-4">
                  <span
                    className={`px-2 py-1 text-xs font-medium rounded ${
                      app.analyst_recommendation === 'approve'
                        ? 'bg-green-100 text-green-700'
                        : app.analyst_recommendation === 'review'
                          ? 'bg-amber-100 text-amber-700'
                          : 'bg-red-100 text-red-700'
                    }`}
                  >
                    {app.analyst_recommendation === 'approve'
                      ? 'Setuju'
                      : app.analyst_recommendation === 'review'
                        ? 'Review'
                        : 'Tolak'}
                  </span>
                </td>
                <td className="p-4">
                  <div className="flex items-center justify-center gap-2">
                    <Link to={`/credit/application/detail?id=${app.id}`}>
                      <button className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-all duration-300 flex items-center gap-1">
                        <Eye className="w-4 h-4" /> Detail
                      </button>
                    </Link>
                    <button
                      onClick={() => onApprove(app)}
                      className="px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all duration-300 flex items-center gap-1"
                    >
                      <CheckCircle className="w-4 h-4" /> Setuju
                    </button>
                    <button
                      onClick={() => onReject(app)}
                      className="px-3 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all duration-300 flex items-center gap-1"
                    >
                      <XCircle className="w-4 h-4" /> Tolak
                    </button>
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
